import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  selectEmailCampaigns,
  selectWhatsappCampaigns,
  selectDonations,
  selectReminders,
  selectTransactions,
  selectComments,
  fetchEmailCampaigns,
  fetchWhatsappCampaigns,
  fetchDonations,
  fetchReminders,
  fetchTransactions,
  fetchComments,
  addComment,
  deleteComment,
  clearContactCache,
} from '../store/slices/contactDetailSlice';

export const useContactDetailCache = (contactId) => {
  const dispatch = useDispatch();

  // Get cached data from Redux
  const emailCampaigns = useSelector((state) => selectEmailCampaigns(state, contactId));
  const whatsappCampaigns = useSelector((state) => selectWhatsappCampaigns(state, contactId));
  const donations = useSelector((state) => selectDonations(state, contactId));
  const reminders = useSelector((state) => selectReminders(state, contactId));
  const transactions = useSelector((state) => selectTransactions(state, contactId));
  const comments = useSelector((state) => selectComments(state, contactId));

  // Load email campaigns (only if not already loaded)
  const loadEmailCampaigns = useCallback(
    async (force = false) => {
      if (!force && emailCampaigns.loaded) {
        return emailCampaigns.data;
      }

      const result = await dispatch(fetchEmailCampaigns(contactId));
      if (fetchEmailCampaigns.fulfilled.match(result)) {
        return result.payload.data;
      }
      return [];
    },
    [contactId, emailCampaigns.loaded, dispatch],
  );

  // Load WhatsApp campaigns (only if not already loaded)
  const loadWhatsappCampaigns = useCallback(
    async (force = false) => {
      if (!force && whatsappCampaigns.loaded) {
        return whatsappCampaigns.data;
      }

      const result = await dispatch(fetchWhatsappCampaigns(contactId));
      if (fetchWhatsappCampaigns.fulfilled.match(result)) {
        return result.payload.data;
      }
      return [];
    },
    [contactId, whatsappCampaigns.loaded, dispatch],
  );

  // Load donations (only if not already loaded)
  const loadDonations = useCallback(
    async (force = false) => {
      if (!force && donations.loaded) {
        return donations.data;
      }

      const result = await dispatch(fetchDonations(contactId));
      if (fetchDonations.fulfilled.match(result)) {
        return result.payload.data;
      }
      return [];
    },
    [contactId, donations.loaded, dispatch],
  );

  // Load reminders (only if not already loaded)
  const loadReminders = useCallback(
    async (force = false) => {
      if (!force && reminders.loaded) {
        return reminders.data;
      }

      const result = await dispatch(fetchReminders(contactId));
      if (fetchReminders.fulfilled.match(result)) {
        return result.payload.data;
      }
      return [];
    },
    [contactId, reminders.loaded, dispatch],
  );

  // Load comments (only if not already loaded)
  const loadComments = useCallback(
    async (force = false) => {
      if (!force && comments.loaded) {
        return comments.data;
      }

      const result = await dispatch(fetchComments(contactId));
      if (fetchComments.fulfilled.match(result)) {
        return result.payload.data;
      }
      return [];
    },
    [contactId, comments.loaded, dispatch],
  );

  // Load transactions (only if not already loaded)
  const loadTransactions = useCallback(
    async (force = false) => {
      if (!force && transactions.loaded) {
        return transactions.data;
      }

      const result = await dispatch(fetchTransactions(contactId));
      if (fetchTransactions.fulfilled.match(result)) {
        return result.payload.data;
      }
      return [];
    },
    [contactId, transactions.loaded, dispatch],
  );

  // Add a comment
  const addCommentToCache = useCallback(
    (comment) => {
      dispatch(addComment({ contactId, comment }));
    },
    [contactId, dispatch],
  );

  // Delete a comment
  const deleteCommentFromCache = useCallback(
    (commentId) => {
      dispatch(deleteComment({ contactId, commentId }));
    },
    [contactId, dispatch],
  );

  // Clear cache for this contact
  const clearCache = useCallback(() => {
    dispatch(clearContactCache({ contactId }));
  }, [contactId, dispatch]);

  return {
    // Data and state
    emailCampaigns,
    whatsappCampaigns,
    donations,
    reminders,
    transactions,
    comments,

    // Load functions
    loadEmailCampaigns,
    loadWhatsappCampaigns,
    loadDonations,
    loadReminders,
    loadTransactions,
    loadComments,

    // Mutation functions
    addCommentToCache,
    deleteCommentFromCache,
    clearCache,
  };
};
